const formatTime = (dateString) => {
  try {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString("tr-TR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (error) {
    return dateString;
  }
};

/** DataLayer için tarih: YYYY-MM-DD. Geçersiz veya 0001-01-01 ise null. */
const formatDateForDataLayer = (dateValue) => {
  if (dateValue == null || dateValue === "") return null;
  try {
    let toParse = dateValue;
    if (typeof dateValue === "string") {
      // API bazen "2025.08.02" veya "2025-08-02T00:00:00" dönebiliyor; noktalı tarih parse edilmez
      const datePart = dateValue.split("T")[0].trim();
      if (/^\d{4}[.-]\d{1,2}[.-]\d{1,2}$/.test(datePart)) {
        toParse = datePart.replace(/\./g, "-");
      }
    }
    const date = new Date(toParse);
    if (Number.isNaN(date.getTime())) return null;
    const y = date.getUTCFullYear();
    if (y < 1900) return null;
    const m = String(date.getUTCMonth() + 1).padStart(2, "0");
    const d = String(date.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  } catch {
    return null;
  }
};

/** ProviderId'ye göre affiliation: 6 -> ets, 7 -> expedia, diğer -> turna */
const getAffiliationFromProviderId = (providerId) => {
  if (providerId === 6) return "ets";
  if (providerId === 7) return "expedia";
  return "turna";
};

/** Address veya Tags hem camelCase hem PascalCase gelebilir */
const getAddressField = (address, ...keys) => {
  if (!address || typeof address !== "object") return null;
  for (const k of keys) {
    const v = address[k];
    if (v != null && v !== "") return v;
  }
  return null;
};

/**
 * hotel_country için öncelik: CountryName (tutarlı isim formatı).
 * Search response'larında bazen sadece Address.Country gelir (örn: "Türkiye").
 */
const getHotelCountryFromAddress = (address) =>
  getAddressField(address, "CountryName", "countryName") ??
  getAddressField(address, "Country", "country") ??
  null;

/** Tags dizisinden name değerlerini "," ile birleştirir */
const getHotelThemeFromTags = (tags) => {
  if (!Array.isArray(tags) || tags.length === 0) return null;
  const names = tags.map((t) => t?.name ?? t?.Name).filter(Boolean);
  return names.length > 0 ? names.join(", ") : null;
};

/** Datalayer: dönmeyen/boş değerler (false, "", undefined, null, boşluk-only) için null basar; tüm eventlerde tutarlılık için kullan */
const toDataLayerValue = (v) =>
  v == null || v === false || (typeof v === "string" && v.trim() === "") ? null : v;

/** Ecommerce payload üst seviye string alanlarını boş string -> null yapar (provider tarafında kullanım için) */
export function normalizeHotelEcommerceForDataLayer(ecommerce) {
  if (!ecommerce || typeof ecommerce !== "object") return ecommerce;
  const out = { ...ecommerce };
  if ("item_list_id" in out) out.item_list_id = toDataLayerValue(out.item_list_id) ?? null;
  if ("item_list_name" in out) out.item_list_name = toDataLayerValue(out.item_list_name) ?? null;
  return out;
}

/** room_type: birden fazla değer virgülle ayrılmışsa virgülden sonra boşluksuz (Standart Oda,Kara Manzaralı,Balkonlu) */
const formatRoomTypeForDataLayer = (v) => {
  if (v == null || typeof v !== "string") return v;
  const trimmed = v.trim();
  return trimmed === "" ? null : trimmed.replace(/\s*,\s*/g, ",");
};

/** Rezervasyon sepeti PensionLabel (RO, BB, vb.) → konaklama tipi metni */
const PENSION_LABEL_TO_ACCOMMODATION = {
  // UI (RoomCard) tarafında RO genelde "Kahvaltı Hariç" olarak gösteriliyor; layer'lar arası tutarlılık için aynı kullanılır.
  RO: "Kahvaltı Hariç",
  BB: "Kahvaltı Dahil",
  HB: "Yarım Pansiyon",
  FB: "Tam Pansiyon",
  AI: "Herşey Dahil",
  UAI: "Ultra Herşey Dahil",
};
const getAccommodationTypeFromPensionLabel = (pensionLabel) => {
  if (pensionLabel == null || pensionLabel === "") return null;
  const key = String(pensionLabel).toUpperCase().trim();
  return PENSION_LABEL_TO_ACCOMMODATION[key] ?? pensionLabel;
};

/**
 * Hotel ürünleri için GA4 ecommerce item objesi üretir
 *
 * UI katmanında normalize edilmiş hotel objeleriyle (mapLandingHotelItem / mapHotelItem)
 * çalışacak şekilde tasarlanmıştır.
 */
export const buildHotelEcommerceItem = ({
  product, // Otel kartı / ürün verisi (mapLandingHotelItem / mapHotelItem çıktısı)
  index,
  item_list_id,
  item_list_name,
  cross_product_promotion,
}) => {
  if (!product) return [];

  const buildItem = (productLike, itemIndex) => {
    const ratingValue =
      typeof productLike?.rating === "number"
        ? productLike.rating
        : typeof productLike?.SurveyScore === "number"
          ? productLike.SurveyScore
          : null;

    const address = productLike?.Address ?? productLike?.address ?? null;
    const tags = productLike?.Tags ?? productLike?.tags ?? null;
    const marketType = productLike?.MarketType ?? productLike?.marketType ?? null;
    const providerId = productLike?.ProviderId ?? productLike?.providerId ?? null;

    // item_category2: MarketType kullan (DOMESTIC -> domestic). Yoksa IsDomestic fallback
    const itemCategory2 =
      marketType != null && String(marketType).trim() !== ""
        ? String(marketType).toLowerCase()
        : productLike?.IsDomestic === false
          ? "international"
          : productLike?.IsDomestic === true
            ? "domestic"
            : null;

    const rawCheckin =
      productLike?.CheckinDate ??
      productLike?.StartDate ??
      productLike?.CheckInDate ??
      productLike?.checkIn;
    const rawCheckout =
      productLike?.CheckoutDate ??
      productLike?.EndDate ??
      productLike?.CheckOutDate ??
      productLike?.checkOut;

    let freeCancellation = null;
    if (
      productLike?.isRefundable !== null &&
      productLike?.isRefundable !== undefined
    ) {
      freeCancellation = String(productLike.isRefundable);
    } else if (productLike?.FreeCancellationAvailable !== undefined) {
      freeCancellation = String(productLike.FreeCancellationAvailable);
    } else if (productLike?.HasFreeCancellation !== undefined) {
      freeCancellation = String(productLike.HasFreeCancellation);
    }

    return {
      item_id:
        String(
          productLike?.TurnaHotelId ??
          productLike?.hotelId ??
          productLike?.HotelId ??
          productLike?.id ??
          productLike?.HotelCode ??
          ""
        ) || null,

      item_name: "hotel",

      hotel_name: toDataLayerValue(
        productLike?.name ??
        productLike?.HotelName ??
        productLike?.Slug ??
        null
      ),

      item_category: "hotel",
      item_category2: toDataLayerValue(itemCategory2),
      item_category3: toDataLayerValue(getAddressField(address, "city", "City") ?? null),
      item_category4: toDataLayerValue(getAddressField(address, "district", "District") ?? null),
      item_category5: toDataLayerValue(getAddressField(address, "line", "Line") ?? null),

      hotel_country: toDataLayerValue(getHotelCountryFromAddress(address)),
      hotel_region: toDataLayerValue(getAddressField(address, "region", "Region") ?? null),
      state: toDataLayerValue(getAddressField(address, "state", "State") ?? null),

      item_list_id: toDataLayerValue(item_list_id) ?? null,
      item_list_name: toDataLayerValue(item_list_name) ?? null,

      coupon: toDataLayerValue(
        (productLike?.PaxPricesSummary?.AppliedCampaignName &&
          productLike.PaxPricesSummary.AppliedCampaignName.join(",")) ||
        null
      ),

      hotel_theme: toDataLayerValue(
        getHotelThemeFromTags(tags) ??
        (Array.isArray(productLike?.Themes) && productLike.Themes?.length > 0
          ? (productLike.Themes[0]?.Name ?? productLike.Themes[0]?.name)
          : null) ??
        productLike?.Theme ??
        productLike?.theme
      ),

      hotel_star_rating: toDataLayerValue(
        productLike?.starRating != null
          ? `${productLike.starRating} Star`
          : productLike?.StarNumber != null
            ? `${productLike.StarNumber} Star`
            : null
      ),

      free_cancellation_available: toDataLayerValue(freeCancellation),

      guest_rating: toDataLayerValue(ratingValue != null ? String(ratingValue) : null),

      room_type:
        toDataLayerValue(
          formatRoomTypeForDataLayer(
            productLike?.roomTypeDescription ??
              productLike?.RoomTypeName ??
              "default",
          ) ?? "default"
        ) ?? null,

      start_date: formatDateForDataLayer(rawCheckin),
      end_date: formatDateForDataLayer(rawCheckout),

      affiliation: getAffiliationFromProviderId(providerId),

      currency:
        productLike?.Currency ??
        productLike?.PriceCurrency ??
        "TRY",

      index: itemIndex,

      cross_product_promotion: toDataLayerValue(
        cross_product_promotion ?? productLike?.CrossProductPromotion ?? null
      ),

      discount: (() => {
        // Net/Gross ayrımı: value/price için netPrice kullanılmalı.
        // UI katmanı genelde productLike.price'ı net'e set eder, priceOriginal gross olur.
        const gross =
          productLike?.priceOriginal ??
          productLike?.PaxPricesSummary?.Total ??
          productLike?.Room?.RoomItems?.[0]?.RoomPrice?.Pricing?.GrossPrice ??
          productLike?.Price;
        const net =
          productLike?.price ??
          productLike?.PaxPricesSummary?.DiscountedTotal ??
          productLike?.Room?.RoomItems?.[0]?.RoomPrice?.Pricing?.NetPrice ??
          productLike?.PaxPricesSummary?.Total;
        const g = Number(gross);
        const n = Number(net);
        if (Number.isFinite(g) && Number.isFinite(n)) {
          const diff = g - n;
          return Math.max(0, Math.round(diff * 100) / 100);
        }
        return 0;
      })(),

      price:
        // items[].price: gross olarak kalmalı (UI/raporlama beklentisi).
        productLike?.priceOriginal ??
        productLike?.PaxPricesSummary?.Total ??
        productLike?.Room?.RoomItems?.[0]?.RoomPrice?.Pricing?.GrossPrice ??
        productLike?.price ??
        productLike?.PaxPricesSummary?.DiscountedTotal ??
        productLike?.Room?.RoomItems?.[0]?.RoomPrice?.Pricing?.NetPrice ??
        productLike?.Price ??
        0,

      quantitiy: 1,
    };
  };

  return [buildItem(product, index)];
};

/**
 * Otel detay sayfasında "Odayı Ayır" (add_to_cart) için tek item üretir.
 * buildHotelEcommerceItem ile aynı alanlar + accommodation_type + quantity (GA4 add_to_cart şeması).
 *
 * @param {Object} product - Otel bilgisi (HotelId, HotelName, City, Country, SurveyScore, StarNumber, IsDomestic, checkIn, checkOut, vb.)
 * @param {Object} roomOption - Seçilen oda/fiyat: { roomId, roomName, accommodationLabel, price, discountRate, refundable, currency }
 * @param {number} index - Item index (0 tabanlı veya 1 tabanlı)
 * @param {string} [item_list_id]
 * @param {string} [item_list_name]
 * @param {string} [cross_product_promotion]
 */
export const buildHotelAddToCartItem = ({
  product,
  roomOption,
  index,
  item_list_id,
  item_list_name,
  cross_product_promotion,
}) => {
  if (!product) return null;

  const ratingValue =
    typeof product?.rating === "number"
      ? product.rating
      : typeof product?.SurveyScore === "number"
        ? product.SurveyScore
        : null;

  const roomName = roomOption?.roomName ?? product?.roomTypeDescription ?? product?.RoomTypeName ?? "default";
  const accommodationLabel = roomOption?.accommodationLabel ?? null;

  const address = product?.Address ?? product?.address ?? null;
  const tags = product?.Tags ?? product?.tags ?? null;
  const marketType = product?.MarketType ?? product?.marketType ?? null;
  const providerId = product?.ProviderId ?? product?.providerId ?? null;

  const itemCategory2 =
    marketType != null && String(marketType).trim() !== ""
      ? String(marketType).toLowerCase()
      : product?.IsDomestic === false
        ? "international"
        : product?.IsDomestic === true
          ? "domestic"
          : null;

  const rawCheckin =
    product?.CheckinDate ?? product?.StartDate ?? product?.CheckInDate ?? product?.checkIn;
  const rawCheckout =
    product?.CheckoutDate ?? product?.EndDate ?? product?.CheckOutDate ?? product?.checkOut;

  let freeCancellation = null;
  if (roomOption?.refundable != null) {
    freeCancellation = String(roomOption.refundable);
  } else if (product?.isRefundable != null) {
    freeCancellation = String(product.isRefundable);
  }

  return {
    // add_to_cart: item_id HotelId olmalı (RoomId değil)
    item_id:
      String(
        product?.TurnaHotelId ??
          product?.hotelId ??
          product?.HotelId ??
          product?.id ??
          product?.HotelCode ??
          ""
      ) || null,
    item_name: "hotel",
    hotel_name: toDataLayerValue(product?.name ?? product?.HotelName ?? product?.Slug ?? null),
    cross_product_promotion: toDataLayerValue(
      cross_product_promotion ?? product?.CrossProductPromotion ?? null
    ),
    item_category: "hotel",
    item_category2: toDataLayerValue(itemCategory2),
    item_category3: toDataLayerValue(getAddressField(address, "city", "City") ?? null),
    item_category4: toDataLayerValue(getAddressField(address, "district", "District") ?? null),
    item_category5: toDataLayerValue(getAddressField(address, "line", "Line") ?? null),
    hotel_country: toDataLayerValue(getHotelCountryFromAddress(address) ?? product?.CountryName ?? product?.countryName ?? null),
    hotel_region: toDataLayerValue(getAddressField(address, "region", "Region") ?? product?.Region ?? product?.RegionName ?? null),
    state: toDataLayerValue(getAddressField(address, "state", "State") ?? null),
    item_list_id: toDataLayerValue(item_list_id) ?? null,
    item_list_name: toDataLayerValue(item_list_name) ?? null,
    coupon: toDataLayerValue(
      (product?.PaxPricesSummary?.AppliedCampaignName &&
        product.PaxPricesSummary.AppliedCampaignName.join(",")) ??
      null
    ),
    hotel_theme: toDataLayerValue(
      getHotelThemeFromTags(tags) ??
      (Array.isArray(product?.Themes) && product.Themes?.length > 0
        ? (product.Themes[0]?.Name ?? product.Themes[0]?.name)
        : null) ??
      product?.Theme ??
      product?.theme
    ),
    hotel_star_rating: toDataLayerValue(
      product?.starRating != null
        ? `${product.starRating} Star`
        : product?.StarNumber != null
          ? `${product.StarNumber} Star`
          : null
    ),
    free_cancellation_available: toDataLayerValue(freeCancellation),
    guest_rating: toDataLayerValue(ratingValue != null ? String(ratingValue) : null),
    room_type: toDataLayerValue(formatRoomTypeForDataLayer(roomName) ?? "default") ?? null,
    accommodation_type: toDataLayerValue(accommodationLabel),
    start_date: formatDateForDataLayer(rawCheckin),
    end_date: formatDateForDataLayer(rawCheckout),
    affiliation: getAffiliationFromProviderId(providerId),
    currency: roomOption?.currency ?? product?.Currency ?? product?.PriceCurrency ?? "TRY",
    index,
    discount: (() => {
      // Net/Gross ayrımı: add_to_cart value/price netPrice olmalı.
      const gross =
        roomOption?.priceOriginal ??
        product?.priceOriginal ??
        roomOption?.price ??
        product?.price;
      const net = roomOption?.price ?? product?.price;
      const g = Number(gross);
      const n = Number(net);
      if (Number.isFinite(g) && Number.isFinite(n)) {
        const diff = g - n;
        return Math.max(0, Math.round(diff * 100) / 100);
      }
      return 0;
    })(),
    price:
      // items[].price: gross olarak kalmalı.
      roomOption?.priceOriginal ??
      product?.priceOriginal ??
      roomOption?.price ??
      product?.price ??
      0,
    quantity: 1,
  };
};

/**
 * Rezervasyon sepeti (basket) API ürününden begin_checkout item objesi üretir.
 * Seçili oda yoksa ilk RoomType ve ilk RoomPrice kullanılır.
 *
 * @param {Object} product - basketData.Products[] elemanı (HotelId, HotelName, HotelDetails, RoomTypes, SelectedRoomTypes, ...)
 * @param {number} index - Item index (0 tabanlı)
 * @param {{ item_list_id?: string, item_list_name?: string }} [options] - Bağlama göre: hotel_search, hotel_payment, hotel_completed vb.
 * @returns {Object|null} dataLayer item objesi veya yetersiz veride null
 */
export const buildHotelBeginCheckoutItemFromBasketProduct = (
  product,
  index,
  options = {}
) => {
  if (!product) return null;

  const selectedRoom =
    product.SelectedRoomTypes?.[0] ?? product.RoomTypes?.[0];
  const selectedPrice = selectedRoom?.RoomPrices?.[0];
  if (!selectedRoom || !selectedPrice?.RoomPricing) return null;

  const details = product.HotelDetails ?? {};
  const address = details;
  const tags = details.Tags ?? details.tags ?? null;
  const marketType = details.MarketType ?? product.MarketType ?? null;
  const providerId = product.ProviderId ?? details.ProviderId ?? null;

  const itemCategory2 =
    marketType != null && String(marketType).trim() !== ""
      ? String(marketType).toLowerCase()
      : product.IsDomestic === false
        ? "international"
        : product.IsDomestic === true
          ? "domestic"
          : null;

  const freeCancellation =
    selectedPrice.CancellationPolicy?.IsRefundable != null
      ? String(selectedPrice.CancellationPolicy.IsRefundable)
      : null;

  const ratingValue =
    typeof details.SurveyScore === "number" ? details.SurveyScore : null;

  const item_list_id = options.item_list_id ?? "hotel_search";
  const item_list_name = options.item_list_name ?? "Hotel Search";
  const couponCode = options?.couponCode ?? null;
  const couponAppliedDiscount = options?.couponAppliedDiscount ?? null;
  // add_to_cart tarafında UI genelde "Kahvaltı Hariç" gibi metin basıyor.
  // Basket tarafında bu metin çoğunlukla PensionName/Pension alanında gelir; varsa onu önceliklendir.
  const accommodationSource =
    selectedPrice.PensionName ??
    selectedPrice.Pension ??
    selectedPrice.PensionLabel ??
    null;

  return {
    item_id: String(product.TurnaHotelId ?? product.HotelId ?? product.Id ?? "").trim() || null,
    item_name: "hotel",
    hotel_name: toDataLayerValue(product.HotelName ?? details.HotelName ?? null),
    cross_product_promotion: toDataLayerValue(product.CrossProductPromotion ?? null),
    item_category: "hotel",
    item_category2: toDataLayerValue(itemCategory2),
    item_category3: toDataLayerValue(getAddressField(address, "city", "City") ?? product.City ?? null),
    item_category4: toDataLayerValue(getAddressField(address, "district", "District") ?? product.District ?? null),
    item_category5: toDataLayerValue(getAddressField(address, "line", "Line", "Address") ?? null),
    hotel_country: toDataLayerValue(getHotelCountryFromAddress(address) ?? details.CountryName ?? details.countryName ?? null),
    hotel_region: toDataLayerValue(getAddressField(address, "region", "Region") ?? details.Region ?? null),
    state: toDataLayerValue(product.IsDomestic ? null : (getAddressField(address, "state", "State") ?? null)),
    item_list_id: toDataLayerValue(item_list_id) ?? null,
    item_list_name: toDataLayerValue(item_list_name) ?? null,
    coupon: toDataLayerValue(
      product.AppliedCampaignName ?? couponCode ?? null,
    ),
    hotel_theme: toDataLayerValue(getHotelThemeFromTags(tags)),
    hotel_star_rating: toDataLayerValue(details.StarNumber != null ? `${details.StarNumber} Star` : null),
    free_cancellation_available: toDataLayerValue(freeCancellation),
    guest_rating: toDataLayerValue(ratingValue != null ? String(ratingValue) : null),
    room_type: toDataLayerValue(formatRoomTypeForDataLayer(selectedRoom.RoomName ?? "default") ?? "default") ?? null,
    accommodation_type: toDataLayerValue(
      // Metin geldiyse (örn: "Kahvaltı Hariç") olduğu gibi kullan; label geldiyse (RO/BB...) map et.
      (typeof accommodationSource === "string" &&
      accommodationSource.trim() !== "" &&
      accommodationSource.trim().length > 3
        ? accommodationSource
        : getAccommodationTypeFromPensionLabel(accommodationSource)),
    ),
    start_date: formatDateForDataLayer(product.CheckinDate),
    end_date: formatDateForDataLayer(product.CheckoutDate),
    affiliation: getAffiliationFromProviderId(providerId),
    currency: selectedPrice.RoomPricing?.Currency ?? "TRY",
    index,
    // GA4 şeması için:
    // - items[].price: indirim uygulanmış (net) tutar
    // - items[].discount: indirim tutarı (rate değil)
    discount: (() => {
      if (couponAppliedDiscount != null && couponAppliedDiscount !== "") {
        const discountFromCoupon = Number(couponAppliedDiscount);
        if (Number.isFinite(discountFromCoupon)) return discountFromCoupon;
      }

      const discount = Number(selectedPrice.RoomPricing?.Discount ?? 0);
      return Number.isFinite(discount) ? discount : 0;
    })(),
    price: (() => {
      const net = Number(selectedPrice.RoomPricing?.NetPrice ?? NaN);
      if (Number.isFinite(net)) return net;
      const gross = Number(selectedPrice.RoomPricing?.GrossPrice ?? NaN);
      if (Number.isFinite(gross)) return gross;
      return 0;
    })(),
    quantity: 1,
  };
};

/**
 * Seyahatlerim sayfası (hash) refund dataLayer item'ı üretir.
 * Hash response Products yapısı: Rooms[], HotelDetails, ReservationCode, Id vb.
 *
 * @param {Object} product - Hash Data.Products[] elemanı
 * @param {number} index - Item index (0 tabanlı)
 * @param {string} [coupon] - Kullanılan kampanya kodu
 * @returns {Object|null} dataLayer item objesi
 */
export const buildHotelRefundItemFromHashProduct = (product, index, coupon = "") => {
  if (!product) return null;

  const room = product.Rooms?.[0];
  const details = product.HotelDetails ?? {};
  const nestedAddress =
    details?.Address && typeof details.Address === "object" ? details.Address : null;
  const address = nestedAddress ?? details;
  const tags = details.Tags ?? details.tags ?? null;
  const marketType = details.MarketType ?? product.MarketType ?? null;
  const providerId = product.ProviderId ?? details.ProviderId ?? null;
  const reservedRoom = room?.ReservedRoom ?? {};
  const priceSummary = room?.PriceSummary ?? product.PriceSummary ?? {};

  const itemCategory2 =
    marketType != null && String(marketType).trim() !== ""
      ? String(marketType).toLowerCase()
      : product.IsDomestic === false
        ? "international"
        : product.IsDomestic === true
          ? "domestic"
          : null;

  const freeCancellation =
    room?.CancellationPolicy?.IsRefundable != null
      ? String(room.CancellationPolicy.IsRefundable)
      : null;

  const price =
    Number(priceSummary.BaseFare) ||
    Number(priceSummary.SaleBaseFare) ||
    Number(priceSummary.TotalFare) ||
    0;
  const discount =
    Number(priceSummary.Discount) ||
    Number(priceSummary.SaleDiscount) ||
    0;

  const city =
    getAddressField(address, "city", "City") ??
    getAddressField(details, "city", "City") ??
    product.City ??
    null;
  const district =
    getAddressField(address, "district", "District") ??
    getAddressField(details, "district", "District") ??
    product.District ??
    null;
  const region =
    getAddressField(address, "region", "Region") ??
    getAddressField(details, "region", "Region") ??
    product.Region ??
    null;
  const country =
    getHotelCountryFromAddress(address) ??
    getHotelCountryFromAddress(details) ??
    details.CountryName ??
    details.countryName ??
    product.CountryName ??
    product.countryName ??
    details.Country ??
    details.country ??
    product.Country ??
    product.country ??
    (product.IsDomestic ? "Türkiye" : null);
  const stateValue = product.IsDomestic
    ? null
    : getAddressField(address, "state", "State") ??
      getAddressField(details, "state", "State") ??
      product.State ??
      product.state ??
      null;

  return {
    item_id: String(product.TurnaHotelId ?? product.HotelId ?? product.Id ?? "").trim() || null,
    item_name: "hotel",
    hotel_name: toDataLayerValue(product.HotelName ?? details.HotelName ?? null),
    item_category: "hotel",
    item_category2: toDataLayerValue(itemCategory2),
    item_category3: toDataLayerValue(city),
    item_category4: toDataLayerValue(district),
    item_category5: toDataLayerValue(getAddressField(address, "line", "Line", "Address") ?? null),
    hotel_country: toDataLayerValue(country),
    hotel_region: toDataLayerValue(region),
    state: toDataLayerValue(stateValue),
    // Refund item seviyesinde kampanya/coupon bilgisini direkt CampaignCode'dan basıyoruz.
    // Veri gelmediğinde null dönmesi için fallback'i kaldırıyoruz.
    coupon: toDataLayerValue(coupon ?? null),
    hotel_theme: toDataLayerValue(getHotelThemeFromTags(tags)),
    hotel_star_rating: toDataLayerValue(details.StarNumber != null ? `${details.StarNumber} Star` : null),
    free_cancellation_available: toDataLayerValue(freeCancellation),
    guest_rating: toDataLayerValue(details.SurveyScore != null ? String(details.SurveyScore) : null),
    room_type: toDataLayerValue(formatRoomTypeForDataLayer(reservedRoom.RoomName ?? "default") ?? "default") ?? null,
    accommodation_type: toDataLayerValue(getAccommodationTypeFromPensionLabel(reservedRoom.PensionName ?? room?.PensionLabel)),
    start_date: formatDateForDataLayer(product.CheckinDate ?? room?.Checkin),
    end_date: formatDateForDataLayer(product.CheckoutDate ?? room?.Checkout),
    affiliation: getAffiliationFromProviderId(providerId),
    currency: priceSummary.Currency ?? "TRY",
    index,
    discount,
    price,
    quantity: 1,
    pnr_number: product.ReservationCode ?? null,
    ticked_id: product.Id != null ? String(product.Id) : null,
  };
};

/**
 * Otel refund (iptal) ecommerce objesi oluşturur.
 * Seyahatlerim sayfasında başarılı iade işleminde dataLayer için.
 *
 * @param {Object} params
 * @param {Array} params.products - Hash Data.Products
 * @param {Object} params.basketData - Data.Basket veya Data
 * @param {Object} params.priceSummary - TotalPriceSummary.PriceSummary veya Rooms[0].PriceSummary
 * @param {string} [params.currency="TRY"]
 * @returns {Object|null} ecommerce objesi
 */
export const buildHotelRefundEcommerce = ({
  products = [],
  basketData = null,
  priceSummary = null,
  currency = "TRY",
}) => {
  if (!Array.isArray(products) || products.length === 0) return null;

  const firstProduct = products[0];
  const firstRoom = firstProduct?.Rooms?.[0];
  const summary = priceSummary ?? firstRoom?.PriceSummary ?? firstProduct?.PriceSummary ?? {};

  const basket = basketData?.Basket ?? basketData ?? {};
  const basketId = basket.BasketId ?? basketData?.BasketId ?? basketData?.Basket?.BasketId;
  const hasBasketId =
    basketId != null &&
    String(basketId).trim() !== "" &&
    Number.isFinite(Number(basketId));

  const finalizedPayment =
    basket?.AllPayments?.find((p) => p?.IsFinalized === true) ?? basket?.AllPayments?.[0];
  const allPayments = basket?.AllPayments ?? [];

  // getBasketByHashCode response tarafında UsedCampaign.CampaignCode bekleniyor.
  // backend farklı isimlerle (Code/Title/Name) döndürürse de fallback veriyoruz.
  const couponCodeRaw =
    basket?.UsedCampaign?.CampaignCode ??
    basket?.UsedCampaign?.Code ??
    basket?.UsedCampaign?.Title ??
    basket?.UsedCampaign?.Name ??
    basketData?.UsedCampaign?.CampaignCode ??
    basketData?.UsedCampaign?.Code ??
    basketData?.UsedCampaign?.Title ??
    basketData?.UsedCampaign?.Name ??
    null;

  const coupon = toDataLayerValue(couponCodeRaw);

  const roomSummaries = products.flatMap((product) =>
    (Array.isArray(product?.Rooms) ? product.Rooms : [])
      .map((room) => room?.PriceSummary)
      .filter(Boolean)
  );
  const sumRoomPriceSummary = (field) => {
    let hasAny = false;
    const total = roomSummaries.reduce((sum, ps) => {
      const value = Number(ps?.[field]);
      if (Number.isFinite(value)) {
        hasAny = true;
        return sum + value;
      }
      return sum;
    }, 0);
    return hasAny ? total : null;
  };

  const roomTotalFare = sumRoomPriceSummary("TotalFare");
  const roomAccommodationTax = sumRoomPriceSummary("AccommodationTax");

  // Purchase ile aynı kaynak/mantık: value için odaların TotalFare toplamı kullanılır.
  const value = roomTotalFare != null ? roomTotalFare : 0;
  // Purchase ile aynı kaynak/mantık: currency odaların PriceSummary.Currency alanından alınır.
  const currencyCode =
    roomSummaries.find((ps) => typeof ps?.Currency === "string")?.Currency ??
    currency?.toUpperCase() ??
    "TRY";
  const accommodationTax = roomAccommodationTax != null
    ? roomAccommodationTax
    : Number(summary?.AccommodationTax);
  const tax = Number.isFinite(accommodationTax) ? accommodationTax : 0;

  // Purchase ile aynı payment_type kurgusu:
  // - Tekil: credit_card veya turna_point
  // - Karma: "credit_card, turna_point"
  const hasTurnaPointPayment =
    (Array.isArray(allPayments) &&
      allPayments.some(
        (p) =>
          p?.IsFinalized === true &&
          p?.PaymentType === 2,
      )) ||
    finalizedPayment?.PaymentType === 2;

  const hasCreditCardPayment =
    (Array.isArray(allPayments) &&
      allPayments.some(
        (p) =>
          p?.IsFinalized === true &&
          p?.PaymentType === 0,
      )) ||
    finalizedPayment?.PaymentType === 0 ||
    // Finalized ödeme var ama type alanı gelmiyorsa, puan yoksa kart varsay.
    (!hasTurnaPointPayment && finalizedPayment != null);

  const payment_type = [
    hasCreditCardPayment ? "credit_card" : null,
    hasTurnaPointPayment ? "turna_point" : null,
  ]
    .filter(Boolean)
    .join(", ");

  const credit_card_type =
    hasCreditCardPayment
      ? (() => {
          const raw =
            finalizedPayment?.CardOrganizationType ??
            finalizedPayment?.CardType ??
            null;
          return raw != null ? String(raw).toLowerCase() : null;
        })()
      : null;
  const credit_card_bank =
    hasCreditCardPayment
      ? finalizedPayment?.BankName ?? allPayments?.[0]?.BankName ?? null
      : null;
  const payment_installment =
    hasCreditCardPayment ? finalizedPayment?.InstallmentCount ?? null : null;

  const guest_adult_number = products.reduce(
    (sum, p) => sum + (Number(p.AdultCount) || Number(p.Rooms?.[0]?.AdultCount) || 0),
    0
  );
  const guest_children_number = products.reduce(
    (sum, p) => sum + (Number(p.ChildCount) || Number(p.Rooms?.[0]?.ChildCount) || 0),
    0
  );
  const passengers = {
    guest_total: guest_adult_number + guest_children_number,
    guest_adult_number,
    guest_children_number,
  };

  const items = products
    .map((p, idx) => buildHotelRefundItemFromHashProduct(p, idx, coupon))
    .filter(Boolean);

  if (items.length === 0) return null;

  return {
    ...(hasBasketId && { transaction_id: String(basketId) }),
    transaction_category: "hotel",
    value,
    shipping: 0,
    tax,
    currency: currencyCode,
    coupon,
    payment_type,
    credit_card_type: credit_card_type ?? null,
    credit_card_bank: credit_card_bank ?? null,
    payment_installment:
      payment_installment != null ? Number(payment_installment) : null,
    passengers,
    items,
  };
};

